// _forms/_public_movil/MovilFormulario.jsx
import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useFormulario from '../_shared/hooks/useFormulario';
import ConditionalModal from '../_shared/components/ConditionalModal';
import { useNotification } from '../../../errnotification/context/NotificationContext';
import { PaymentModal } from '../../../payment';
import { formatters } from '../_shared/utils/formatters';
import { validators } from '../_shared/utils/validators';
import DateSelectors from '../_shared/components/DateSelectors';
import BannerRenderer from '../_shared/components/BannerRenderer';
import './MovilFormulario.css';

const MovilFormulario = ({
  isEditable = false,
  selectedPackage = null,
  isFillingForm = true,
  onTabChange,
}) => {
  const { slug } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [currentStep, setCurrentStep] = useState(0);
  const { showNotification } = useNotification();

  // ==========================================
  // 1. HOOK PRINCIPAL
  // ==========================================
  const {
    // Estados
    formConfig,
    setFormConfig,
    paquetes,
    selectedPaquete,
    setSelectedPaquete,
    selectedAddons,
    setSelectedAddons,
    aceptaDeslinde,
    setAceptaDeslinde,
    deletedFields,
    error,
    isLoading,
    resumenModal,
    modalOpen,
    activeModal,
    selectedField,
    existingConditions,
    conditions,
    optionConditionModal,
    totalAmount,
    amountPerPerson,
    numberOfCompetitors,
    // Setters
    setModalOpen,
    setActiveModal,
    setSelectedField,
    setExistingConditions,
    setConditions,
    setOptionConditionModal,
    // Funciones
    shouldShowOption,
    shouldRenderField,
    validateForm,
    isFormValid,
    calculateResumen,
    handlePaqueteChange,
    handleOpenModal,
    handleCloseModal,
    handleButtonClick,
    handleSaveConditions,
    handleSave,
    // Handlers
    handleFieldChangeWrapper,
    handleAddFieldWrapper,
    handleDeleteFieldWrapper,
    handleAddOptionWrapper,
    handleDeleteOptionWrapper,
    handleOptionChangeWrapper,
    handleOptionConditionsWrapper,
    // 🟢 NUEVO: funciones para categorías
    calculateAge,
    getAutoCategoryRule,
    getVisibleCategoryRules,
  } = useFormulario({
    slug,
    baseUrl,
    selectedPackage,
    isEditable,
    isFillingForm,
    onTabChange,
  });

  // ==========================================
  // 🔍 FUNCIÓN PARA OBTENER EDAD DESDE LA FECHA REAL
  // ==========================================
  const getComputedAge = useCallback((fields) => {
    const dateField = fields.find((f) => {
      const esFechaPorTipo = f.type === 'date';
      const esFechaPorLabel =
        f.label?.toLowerCase().includes('fecha') ||
        f.label?.toLowerCase().includes('nacimiento') ||
        f.label?.toLowerCase().includes('cumpleaños');
      const esFechaPorDataType = f.dataType === 'date';
      return esFechaPorTipo || esFechaPorLabel || esFechaPorDataType;
    });
    if (!dateField?.value) return null;
    return calculateAge(dateField.value);
  }, [calculateAge]);

  // ✅ Los efectos de sincronización ya los hace el hook useFormulario
  // No necesitamos duplicarlos aquí

  // ==========================================
  // 2. OBTENER PASOS DEL FORMULARIO
  // ==========================================
  const getVisibleFields = () => {
    if (!formConfig?.fields?.fields || !Array.isArray(formConfig.fields.fields)) {
      return [];
    }
    return formConfig.fields.fields
      .filter((field) => !field.isPackageInclude && !deletedFields.includes(field.id))
      .filter((field) => isEditable || shouldRenderField(field));
  };

  const visibleFields = getVisibleFields();
  const totalSteps = visibleFields.length + 2; // paquete + campos + deslinde

  // ==========================================
  // 3. HANDLER DE "SIGUIENTE" CON VALIDACIÓN CENTRALIZADA
  // ==========================================
  const handleNextStep = (field) => {
    // Usamos validators.validateFieldExtended para mantener consistencia
    const fieldErrors = validators.validateFieldExtended(field, field.value);

    // Validación adicional específica para fechas
    if (field.type === 'date') {
      if (field.required && (!field.value || String(field.value).trim() === '')) {
        if (!fieldErrors.includes('Este campo es obligatorio.')) {
          fieldErrors.unshift('Debes seleccionar una fecha completa.');
        }
      }
      if (field.value && String(field.value).trim() !== '') {
        const date = new Date(field.value);
        if (isNaN(date.getTime())) {
          if (!fieldErrors.some((e) => e.includes('inválid'))) {
            fieldErrors.push('La fecha seleccionada no es válida.');
          }
        }
      }
    }

    if (fieldErrors.length > 0) {
      // Marcar el campo como inválido en el formConfig para que se vea el error visual
      setFormConfig((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          fields: prev.fields.fields.map((f) =>
            f.id === field.id
              ? { ...f, invalid: true, errorMessage: fieldErrors[0] }
              : f
          ),
        },
      }));
      showNotification(
        `❌ ${fieldErrors.join('\n')}`,
        'error',
        5000
      );
      return;
    }

    // Si todo está bien, limpiar estado inválido y avanzar
    setFormConfig((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        fields: prev.fields.fields.map((f) =>
          f.id === field.id ? { ...f, invalid: false, errorMessage: '' } : f
        ),
      },
    }));
    setCurrentStep(currentStep + 1);
  };

  // ==========================================
  // 4. RENDERIZADO MÓVIL
  // ==========================================
  const renderStepIndicator = () => (
    <div className="movil-step-indicator">
      <span className="movil-step-text">
        Paso {Math.min(currentStep + 1, totalSteps)} de {totalSteps}
      </span>
      <div className="movil-progress-bar">
        <div
          className="movil-progress-fill"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderPaqueteStep = () => (
    <div className="movil-step movil-paquete-step">
      <h2 className="movil-step-title">🎯 Elige tu Paquete</h2>
      <p className="movil-step-desc">Selecciona el paquete que mejor se adapte a ti.</p>
      <div className="movil-paquete-container">
        <select
          className="movil-paquete-select"
          value={selectedPaquete || ''}
          onChange={handlePaqueteChange}
        >
          <option value="">Selecciona un paquete</option>
          {paquetes.map((paquete) => (
            // ✅ CORREGIDO: Usar id_package o id como fallback
            <option 
              key={paquete.id_package || paquete.id} 
              value={paquete.id_package || paquete.id}
            >
              {paquete.title || paquete.name}
            </option>
          ))}
        </select>
      </div>
      {selectedPaquete && (
        <div className="movil-package-preview">{renderAmountSection()}</div>
      )}
      <button
        className="movil-next-btn"
        onClick={() => setCurrentStep(currentStep + 1)}
        disabled={!selectedPaquete}
      >
        Siguiente →
      </button>
    </div>
  );

  const renderAmountSection = () => {
    if (!selectedPaquete) return null;
  const paquete = paquetes.find((p) => String(p.id_package) === String(selectedPaquete) || 
  String(p.id) === String(selectedPaquete));
    if (!paquete) return null;

    const amtPerPerson = parseFloat(paquete.amount) || 0;
    const numCompetitors = parseInt(paquete.number_of_competitors) || 1;
    const addons = paquete.addons || [];

    const renderAddonControl = (addon) => {
      const maxQty = numCompetitors;
      const currentQty = selectedAddons[addon.id] || 0;

      if (numCompetitors === 1) {
        return (
          <input
            type="checkbox"
            checked={currentQty > 0}
            onChange={(e) => {
              setSelectedAddons((prev) => ({
                ...prev,
                [addon.id]: e.target.checked ? 1 : 0,
              }));
            }}
            className="movil-addon-checkbox"
          />
        );
      }

      return (
        <div className="movil-addon-quantity">
          <button
            className="movil-qty-btn"
            onClick={() => {
              if (currentQty > 0) {
                setSelectedAddons((prev) => ({
                  ...prev,
                  [addon.id]: currentQty - 1,
                }));
              }
            }}
          >
            −
          </button>
          <span className="movil-qty-value">{currentQty}</span>
          <button
            className="movil-qty-btn"
            onClick={() => {
              if (currentQty < maxQty) {
                setSelectedAddons((prev) => ({
                  ...prev,
                  [addon.id]: currentQty + 1,
                }));
              }
            }}
          >
            +
          </button>
        </div>
      );
    };

    return (
      <div className="movil-package-details">
        <div className="movil-package-row">
          <span className="movil-package-label">Por persona:</span>
          <span className="movil-package-value">
            {formatters.formatCurrency(amtPerPerson, paquete.currency)}
          </span>
        </div>
        <div className="movil-package-row">
          <span className="movil-package-label">Competidores:</span>
          <span className="movil-package-value movil-competitor-badge">
            {numCompetitors}{' '}
            {numCompetitors === 1
              ? '(Individual)'
              : `(Equipo de ${numCompetitors})`}
          </span>
        </div>
        {addons.length > 0 && (
          <div className="movil-addons-section">
            <span className="movil-package-label">Opciones adicionales:</span>
            {addons.map((addon) => {
              const qty = selectedAddons[addon.id] || 0;
              const subtotal = (
                parseFloat(addon.extra_amount || 0) * qty
              ).toFixed(2);
              return (
                <div key={addon.id} className="movil-addon-item">
                  {renderAddonControl(addon)}
                  <span className="movil-addon-label">{addon.label}</span>
                  <span className="movil-addon-price">
                    +{formatters.formatCurrency(subtotal, paquete.currency)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div className="movil-package-row movil-total-row">
          <span className="movil-package-label movil-total-label">Total:</span>
          <span className="movil-package-value movil-total-value">
            {formatters.formatCurrency(totalAmount, paquete.currency)}
          </span>
        </div>
      </div>
    );
  };

  const renderFieldStep = (field, index) => {
    const fieldIndex = index + 1;
    if (currentStep !== fieldIndex) return null;
  
    const fieldValue = field.value !== undefined && field.value !== null ? field.value : '';
    const isInvalid = field.invalid ? 'movil-invalid-field' : '';
  
    return (
      <div key={field.id} className="movil-step movil-field-step">
        <h2 className="movil-step-title">{field.label}</h2>
        {field.required && <span className="movil-required-badge">Obligatorio</span>}
        <div className={`movil-field-container ${isInvalid}`}>
          {/* Campo de tipo Banner Informativo / Aviso */}
          {field.type === 'banner' && (
            <div className="movil-banner-field" style={{
              backgroundColor: '#eef6ff',
              borderLeft: '4px solid #1890ff',
              padding: '12px 16px',
              borderRadius: '6px',
              color: '#1f2937',
              fontSize: '0.95rem',
              lineHeight: '1.5',
              marginBottom: '15px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>ℹ️</span> {field.label}
              </div>
              <BannerRenderer content={field.value} />
            </div>
          )}

          {/* ✅ Campo Categoría con lógica ACTUALIZADA */} 
          {field.type === 'category' && (
            <div className="movil-category-field">
              <select
                value={fieldValue}
                onChange={(e) => {
                  if (field.isUserSelected) {
                    setFormConfig((prev) => ({
                      ...prev,
                      fields: {
                        ...prev.fields,
                        fields: prev.fields.fields.map((f) =>
                          f.id === field.id ? { ...f, value: e.target.value } : f
                        ),
                      },
                    }));
                  }
                }}
                disabled={!field.isUserSelected}
                className={`movil-field-select ${
                  field.invalid ? 'movil-invalid-select' : ''
                } ${!field.isUserSelected ? 'movil-locked-field' : ''}`}
              >
                <option value="">
                  {field.isUserSelected
                    ? 'Selecciona una categoría'
                    : 'Categoría asignada automáticamente'}
                </option>
                {getVisibleCategoryRules(
                  field.categoryRules || [],
                  getComputedAge(formConfig.fields.fields),
                  field.isUserSelected
                ).map((rule, rIdx) => (
                  <option key={rIdx} value={rule.label}>
                    {rule.label}
                    {!rule.isSpecial && ` (${rule.minAge}-${rule.maxAge} años)`}
                  </option>
                ))}
              </select>

              {/* ✅ Checkbox SIEMPRE visible para campos category */}
              <label className="movil-special-category-toggle">
                <input
                  type="checkbox"
                  checked={field.isUserSelected || false}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setFormConfig((prev) => {
                      const computedAge = getComputedAge(prev.fields.fields);
                      const currentRules = field.categoryRules || [];

                      let autoValue = field.value;
                      if (!isChecked && computedAge !== null) {
                        const rule = getAutoCategoryRule(currentRules, computedAge);
                        autoValue = rule ? rule.label : '';
                      }

                      return {
                        ...prev,
                        fields: {
                          ...prev.fields,
                          fields: prev.fields.fields.map((f) =>
                            f.id === field.id
                              ? {
                                  ...f,
                                  isUserSelected: isChecked,
                                  value: autoValue,
                                }
                              : f
                          ),
                        },
                      };
                    });
                  }}
                  className="movil-special-checkbox"
                />
                <span className="movil-special-text">
                  Quiero cambiar de categoría
                </span>
              </label>

              <small className="movil-help-text">
                💡 Categoría sugerida automáticamente según tu edad y paquete. Si quieres elegir otra categoría, marca la casilla.
              </small>
            </div>
          )}

          {/* ✅ Campo Edad bloqueado (solo lectura) */}
          {field.type === 'text' &&
            field.dataType === 'integer' &&
            (field.label?.toLowerCase().includes('edad') ||
              field.label?.toLowerCase() === 'edad') && (
              <div className="movil-age-field">
                <input
                  type="text"
                  value={field.value || ''}
                  readOnly
                  disabled
                  className="movil-field-input movil-locked-field"
                  placeholder="Se calcula automáticamente"
                />
                <small className="movil-help-text">
                  🔒 Tu edad se calcula automáticamente desde tu fecha de
                  nacimiento
                </small>
              </div>
            )}

          {/* Campos normales (texto, número, email) */}
          {field.type === 'text' &&
            field.dataType === 'integer' &&
            !(
              field.label?.toLowerCase().includes('edad') ||
              field.label?.toLowerCase() === 'edad'
            ) && (
              <input
                type="number"
                value={field.value || ''}
                onChange={(e) =>
                  handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)
                }
                className={`movil-field-input ${
                  field.invalid ? 'movil-invalid-input' : ''
                }`}
                placeholder="Ingresa un número"
                inputMode="numeric"
              />
            )}

          {field.type === 'date' && (
            <div className="movil-field-wrapper">
              <DateSelectors
                value={field.value || ''}
                onChange={(dateStr) => {
                  if (!dateStr || dateStr.trim() === '') {
                    if (field.required) {
                      const event = { target: { value: '', name: field.label } };
                      handleFieldChangeWrapper(
                        event,
                        field.id,
                        setFormConfig,
                        isFillingForm
                      );
                    }
                    return;
                  }
                  const event = { target: { value: dateStr, name: field.label } };
                  handleFieldChangeWrapper(
                    event,
                    field.id,
                    setFormConfig,
                    isFillingForm
                  );
                }}
                required={field.required}
                disabled={!isEditable && !isFillingForm}
                className={field.invalid ? 'error' : ''}
              />
              {field.invalid && (
                <p className="movil-error-message">{field.errorMessage}</p>
              )}
            </div>
          )}

          {field.type === 'text' && field.dataType === 'email' && (
            <input
              type="email"
              value={field.value || ''}
              onChange={(e) =>
                handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)
              }
              className={`movil-field-input ${
                field.invalid ? 'movil-invalid-input' : ''
              }`}
              placeholder="ejemplo@correo.com"
              inputMode="email"
            />
          )}

          {field.type === 'text' &&
            !['integer', 'email'].includes(field.dataType) &&
            !(
              field.label?.toLowerCase().includes('edad') ||
              field.label?.toLowerCase() === 'edad'
            ) && (
              <input
                type="text"
                value={field.value || ''}
                onChange={(e) =>
                  handleFieldChangeWrapper(
                    e,
                    field.id,
                    setFormConfig,
                    isFillingForm
                  )
                }
                className={`movil-field-input ${
                  field.invalid ? 'movil-invalid-input' : ''
                }`}
                placeholder={`Ingresa ${field.label.toLowerCase()}`}
              />
            )}

          {field.type === 'select' && (
            <select
              value={fieldValue}
              onChange={(e) =>
                handleFieldChangeWrapper(e, field.id, setFormConfig, isFillingForm)
              }
              className={`movil-field-select ${
                field.invalid ? 'movil-invalid-select' : ''
              }`}
            >
              <option value="">Selecciona una opción</option>
              {field.options
                .filter((opt) => isEditable || shouldShowOption(opt))
                .map((opt, optionIndex) => {
                  const label = typeof opt === 'string' ? opt : opt.label;
                  return (
                    <option key={optionIndex} value={label}>
                      {label}
                    </option>
                  );
                })}
            </select>
          )}

          {field.invalid && (
            <p className="movil-error-message">{field.errorMessage}</p>
          )}
        </div>
        <div className="movil-nav-buttons">
          <button className="movil-next-btn" onClick={() => handleNextStep(field)}>
            Siguiente →
          </button>
          <button
            className="movil-prev-btn"
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            ← Atrás
          </button>
        </div>
      </div>
    );
  };

  const renderDeslindeStep = () => {
    if (currentStep !== totalSteps - 1) return null;
    return (
      <div className="movil-step movil-deslinde-step">
        <h2 className="movil-step-title">📋 Deslinde de Responsabilidades</h2>
        <div className="movil-deslinde-texto">
          <p>
            Al marcar esta casilla, declaro que he sido informado(a) y acepto los
            términos y condiciones del evento. Entiendo que la organización no se
            hace responsable por daños, pérdidas o accidentes que pudieran ocurrir
            durante la participación en el evento.
          </p>
          <p>
            Confirmo que los datos proporcionados son verídicos y autorizo su
            tratamiento según las políticas de privacidad.
          </p>
        </div>
        <label className="movil-deslinde-checkbox">
          <input
            type="checkbox"
            checked={aceptaDeslinde}
            onChange={(e) => setAceptaDeslinde(e.target.checked)}
          />
          <span style={{ color: 'black' }}>
            Acepto el deslinde de responsabilidades{' '}
            <span className="movil-required-asterisk">*</span>
          </span>
        </label>
        <div className="movil-nav-buttons">
          <button
            className="movil-prev-btn"
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            ← Atrás
          </button>
          <button
            className="movil-submit-btn"
            onClick={handleOpenModal}
            disabled={!isFormValid()}
          >
            {!isFormValid() ? '🔒 Completa todo' : '✅ Enviar Inscripción'}
          </button>
        </div>
      </div>
    );
  };

  // ==========================================
  // 5. RENDER PRINCIPAL
  // ==========================================
  if (isLoading) return <div className="movil-loading">⏳ Cargando...</div>;
  if (error) return <div className="movil-error">❌ Error: {error}</div>;

  return (
    <div className="movil-formulario-container" id="movil-formulario">
      <h1 className="movil-formulario-titulo">📝 Inscripción</h1>
      {renderStepIndicator()}
      <div className="movil-steps-container">
        {currentStep === 0 && renderPaqueteStep()}
        {visibleFields.map((field, index) => renderFieldStep(field, index))}
        {renderDeslindeStep()}
      </div>

      {/* Modales */}
      {optionConditionModal && (
        <ConditionalModal
          isOpen={true}
          onClose={() => setOptionConditionModal(null)}
          fieldId={optionConditionModal.fieldId}
          fields={formConfig.fields.fields}
          paquetes={paquetes || []}
          existingConditions={optionConditionModal.option.conditions || []}
          onSave={(payload) => {
            const newConditions = Array.isArray(payload)
              ? payload
              : payload.conditions || [];
            const operator = Array.isArray(payload)
              ? 'OR'
              : payload.operator || 'OR';
            handleOptionConditionsWrapper(
              optionConditionModal.fieldId,
              optionConditionModal.optionIndex,
              newConditions,
              operator,
              setFormConfig
            );
            setOptionConditionModal(null);
          }}
        />
      )}

      {activeModal === 'condicional' && (
        <ConditionalModal
          isOpen={true}
          onClose={handleCloseModal}
          fieldId={selectedField}
          fields={formConfig.fields.fields}
          conditions={conditions}
          setConditions={setConditions}
          paquetes={paquetes || []}
          onSave={handleSaveConditions}
          existingConditions={existingConditions}
        />
      )}

      {modalOpen && resumenModal && (
        <PaymentModal
          resumen={resumenModal}
          onClose={handleCloseModal}
          onSubmit={() => {
            handleCloseModal();
          }}
          onTabChange={onTabChange}
          slug={slug}
        />
      )}
    </div>
  );
};

export default MovilFormulario;